import { Controller } from "@hotwired/stimulus"
import ApexCharts from "apexcharts"

// Stacked daily column chart for one phase of the hospital-rates pipeline.
// Two of these stack vertically on the stats page (discovery above import),
// sharing the same daily x-axis, so the controller is parameterized:
//   series:      [{ key, name, color }, ...] — key indexes into each day object
//   target:      Number (optional)           — dashed reference line; 0 = none
//   hideXLabels: Boolean                      — suppress date labels so the top
//                                               chart defers to the bottom one
//                                               for the shared axis
//   height:      Number                       — chart height in px
// A fixed y-axis label gutter keeps the two charts' plot areas left-aligned
// even when their magnitudes differ (discovery ~thousands, import ~tens), so
// a given day sits at the same x in both charts.
export default class extends Controller {
  static values = {
    days: Array,
    series: Array,
    target: Number,
    hideXLabels: Boolean,
    height: Number
  }

  connect() {
    const days = this.daysValue || []
    const seriesDefs = this.seriesValue || []
    if (days.length === 0 || seriesDefs.length === 0) return

    const categories = days.map((d) => d.date)
    const target = this.targetValue || 0

    const options = {
      chart: {
        type: "bar",
        stacked: true,
        height: this.heightValue || 240,
        toolbar: { show: false },
        fontFamily: "'Neue Haas Grotesk Text Pro','DM Sans',sans-serif"
      },
      series: seriesDefs.map((s) => ({
        name: s.name,
        data: days.map((d) => d[s.key] || 0)
      })),
      colors: seriesDefs.map((s) => s.color),
      plotOptions: {
        bar: {
          borderRadius: 3,
          borderRadiusApplication: "end",
          borderRadiusWhenStacked: "last",
          columnWidth: "60%"
        }
      },
      xaxis: {
        categories: categories,
        type: "category",
        labels: {
          show: !this.hideXLabelsValue,
          style: { colors: "#9CA3AF", fontSize: "11px" },
          rotate: -45,
          formatter: (v) => {
            // v is a YYYY-MM-DD string from Rails; parse as local time, not
            // UTC midnight (which would render as the previous day for
            // viewers in negative-offset time zones).
            const m = typeof v === "string" && v.match(/^(\d{4})-(\d{2})-(\d{2})/)
            if (!m) return v
            const d = new Date(parseInt(m[1], 10), parseInt(m[2], 10) - 1, parseInt(m[3], 10))
            return d.toLocaleDateString("en-US", { month: "short", day: "numeric" })
          }
        },
        axisBorder: { color: "#E5E7EB" }
      },
      yaxis: {
        min: 0,
        forceNiceScale: true,
        labels: {
          // Pin the gutter width so both stacked charts align on x.
          minWidth: 40,
          maxWidth: 40,
          style: { colors: "#9CA3AF", fontSize: "11px" },
          formatter: (v) => Math.round(v).toLocaleString()
        }
      },
      grid: { borderColor: "#F0F1F3", strokeDashArray: 3 },
      dataLabels: { enabled: false },
      legend: {
        position: "top",
        horizontalAlign: "right",
        fontSize: "12px",
        labels: { colors: "#6B7280" }
      },
      tooltip: {
        shared: true,
        intersect: false,
        y: { formatter: (v) => v.toLocaleString() }
      },
      annotations: target > 0 ? {
        yaxis: [{
          y: target,
          borderColor: "#6366F1",
          strokeDashArray: 6,
          label: {
            borderColor: "#6366F1",
            style: { color: "#fff", background: "#6366F1", fontSize: "10px" },
            text: `target ${target}/day`,
            position: "right"
          }
        }]
      } : {}
    }

    this.chart = new ApexCharts(this.element, options)
    this.chart.render()
  }

  disconnect() {
    if (this.chart) this.chart.destroy()
  }
}
